<template>
  <div v-if="payload" @click="openModal" class="cursor_pointer">
    <img v-if="show_add_button" class="btn" src="/edit_icon.svg" width="24px">
    <div v-else>
      <div v-if="mandatory_skills.length > 0">
        <div class="bold is-underlined is-size-6">Mandatory :</div>
        <template v-for="(skill, idx) in mandatory_skills">
          <div class="tag is-rounded m-1" :key="idx" :class="{bold: skill.is_tagged, 'is-warning': skill.is_tagged}">
            <span v-if="skill.is_tagged">#</span> {{skill.skill.name}}
            <span v-if="glyphFor(skill)" class="ver-glyph" :class="glyphClassFor(skill)">{{glyphFor(skill)}}</span>
          </div>
          <span v-if="detailFor(skill)" :key="`vm-${idx}`" class="has-text-grey-light ver-detail" :title="detailFor(skill)">({{truncate(detailFor(skill))}})</span>
        </template>
      </div>
      <div v-if="grouped_skills.length > 0">
        <div class="bold is-underlined is-size-6">Grouped :</div>
        <template v-for="(skill, idx) in grouped_skills">
          <div class="tag is-rounded m-1" :key="idx" :class="{bold: skill.is_tagged, 'is-warning': skill.is_tagged}">
            <span v-if="skill.is_tagged">#</span> {{skill.skill.name}}
            <span v-if="glyphFor(skill)" class="ver-glyph" :class="glyphClassFor(skill)">{{glyphFor(skill)}}</span>
          </div>
          <span v-if="detailFor(skill)" :key="`vg-${idx}`" class="has-text-grey-light ver-detail" :title="detailFor(skill)">({{truncate(detailFor(skill))}})</span>
        </template>
      </div>
      <div v-if="optional_skills.length > 0">
        <div class="bold is-underlined is-size-6">Optional :</div>
        <template v-for="(skill, idx) in optional_skills">
          <div class="tag is-rounded m-1" :key="idx" :class="{bold: skill.is_tagged, 'is-warning': skill.is_tagged}">
            <span v-if="skill.is_tagged">#</span> {{skill.skill.name}}
            <span v-if="glyphFor(skill)" class="ver-glyph" :class="glyphClassFor(skill)">{{glyphFor(skill)}}</span>
          </div>
          <span v-if="detailFor(skill)" :key="`vo-${idx}`" class="has-text-grey-light ver-detail" :title="detailFor(skill)">({{truncate(detailFor(skill))}})</span>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import { mapMutations } from 'vuex'

// אורך מקסימלי לפירוט שמוצג ליד כישור. הטקסט המלא תמיד נשאר ב-title.
const DETAIL_MAX_CHARS = 120

export default {
  name: 'display-skills',
  props: {
    payload: {
      type: Object,
      default: () => {}
    },
    // פירוט הכישורים מהשיחה עם המועמד (conv_svc). null = המודל נפתח בלי מועמד
    // או שהטעינה נכשלה — ואז הרכיב מוצג בדיוק כמו קודם.
    verifications: {
      type: Array,
      default: null
    }
  },
  data () {
    return {
      grouped_skills: null,
      mandatory_skills: null,
      optional_skills: [],
      show_add_button: false
    }
  },
  computed: {
    // כלל ההתאמה בין כישור של המשרה לשורת אימות מהשיחה נמצא רק כאן: שני הצדדים
    // מנורמלים באותה צורה (רווחים בקצוות + רצפי רווחים בפנים), כי אותו כישור
    // נכתב לפעמים עם רווח עודף בהגדרת המשרה.
    verificationsByKey () {
      const map = {}
      if (!Array.isArray(this.verifications)) return map
      this.verifications.forEach(verification => {
        const key = this.normalizeSkillName(verification && verification.skill_key)
        // בלי דריסה: השרת כבר מחזיר שורה אחת לכל כישור, העדכנית ביותר.
        if (key && !map[key]) map[key] = verification
      })
      return map
    },
    // כמה מכישורי המשרה קיבלו בפועל פירוט מהשיחה. משודר החוצה כדי שההורה יוכל
    // לכתוב "פירוט מהשיחה מוצג ליד כל כישור" רק כשבאמת יש מה להראות — מועמד
    // שדיבר על כישורים של משרה אחרת לא אמור לייצר כותרת שמבטיחה כלום.
    matchedSkillCount () {
      const all = (this.mandatory_skills || [])
        .concat(this.grouped_skills || [], this.optional_skills || [])
      return all.filter(skill => this.verificationFor(skill)).length
    }
  },
  watch: {
    matchedSkillCount: {
      immediate: true,
      handler (count) {
        this.$emit('verifications-matched', count)
      }
    }
  },
  created () {
    this.grouped_skills = this.payload.positions_skills.filter(el => el.group_name)
    this.mandatory_skills = this.payload.positions_skills.filter(el => !el.group_name && el.importance == 8)
    if (this.grouped_skills.length == 0 && this.mandatory_skills.length == 0) {
      this.show_add_button = true
    }
    // this.optional_skills = this.payload.positions_skills.filter(el => !el.group_name && el.importance == 2)
  },
  methods: {
    normalizeSkillName (name) {
      return String(name == undefined ? '' : name).trim().replace(/\s+/g, ' ')
    },
    verificationFor (skill) {
      const name = skill && skill.skill ? skill.skill.name : null
      const key = this.normalizeSkillName(name)
      return key ? this.verificationsByKey[key] : undefined
    },
    // ✓ / ✗ / ~ בלבד. pending / skipped / timeout = המועמד לא ענה, ואין מה לומר.
    glyphFor (skill) {
      const verification = this.verificationFor(skill)
      if (!verification) return ''
      const glyphs = { verified_yes: '✓', verified_no: '✗', partial: '~' }
      return glyphs[verification.status] || ''
    },
    glyphClassFor (skill) {
      const verification = this.verificationFor(skill)
      if (!verification) return ''
      const classes = { verified_yes: 'ver-yes', verified_no: 'ver-no', partial: 'ver-partial' }
      return classes[verification.status] || ''
    },
    detailFor (skill) {
      const verification = this.verificationFor(skill)
      return verification && verification.detail ? verification.detail : ''
    },
    truncate (text) {
      const value = String(text == undefined ? '' : text)
      return value.length > DETAIL_MAX_CHARS ? `${value.slice(0, DETAIL_MAX_CHARS - 1)}…` : value
    },
    openModal () {
      this.create({
        designator: 'message-modal',
        component_name: 'PositionSkills',
        resource_data: this.payload,
        additional_data: this.additional_data,
        is_submitable: true,
        position_id: this.payload.id,
        company_id: this.payload.company.id,
        modal_title: `Position Skills - ${this.payload.title} (id: ${this.payload.id})`
      })
    },
    ...mapMutations('modals', ['create'])
  }
}
</script>
<style scoped>
.is-underlined {
  text-decoration: underline;
}
.m-1 {
  margin: 0.25rem;
}
.ver-glyph {
  margin-right: 0.25rem;
  margin-left: 0.25rem;
  font-weight: bold;
}
.ver-yes {
  color: #257953;
}
.ver-no {
  color: #cc0f35;
}
.ver-partial {
  color: #b06500;
}
.ver-detail {
  font-size: 0.8rem;
}
</style>
